export interface ProspectIdentity {
  id: string;
  linkedinUrl: string | null;
  linkedinProfileKey: string | null;
}

type ProspectPage<T extends ProspectIdentity> = {
  prospects: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  matchingTotal: number;
  availableTotal: number;
  awaitingReviewTotal: number;
};

function keyFromLinkedInUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    if (host !== 'linkedin.com' && !host.endsWith('.linkedin.com')) return null;

    const parts = url.pathname.split('/').filter(Boolean);
    const identifier =
      parts[0]?.toLowerCase() === 'in'
        ? parts[1]
        : parts[0]?.toLowerCase() === 'talent' && parts[1]?.toLowerCase() === 'profile'
          ? parts[2]
          : null;
    if (!identifier) return null;
    return decodeURIComponent(identifier).trim().toLowerCase() || null;
  } catch {
    return null;
  }
}

/** Uses the LinkedIn profile URL as the canonical identity, then the stored key. */
export function linkedinProfileIdentityKey(lead: ProspectIdentity): string | null {
  const fromUrl = keyFromLinkedInUrl(lead.linkedinUrl);
  if (fromUrl) return fromUrl;

  const storedKey = lead.linkedinProfileKey?.trim();
  if (!storedKey) return null;
  return keyFromLinkedInUrl(storedKey) ?? storedKey.toLowerCase();
}

/** Removes every queue row for a LinkedIn identity once that person is reviewed. */
export function removeProspectsMatchingReviewedLead<T extends ProspectIdentity>(
  current: ProspectPage<T> | undefined,
  reviewedLead: ProspectIdentity
): ProspectPage<T> | undefined {
  if (!current) return current;

  const reviewedIdentity = linkedinProfileIdentityKey(reviewedLead);
  const removed = current.prospects.filter((prospect) => {
    if (prospect.id === reviewedLead.id) return true;
    return Boolean(reviewedIdentity && linkedinProfileIdentityKey(prospect) === reviewedIdentity);
  });
  if (removed.length === 0) return current;

  const total = Math.max(0, current.total - removed.length);
  return {
    ...current,
    prospects: current.prospects.filter((prospect) => !removed.includes(prospect)),
    total,
    totalPages: Math.ceil(total / current.pageSize),
    matchingTotal: Math.max(0, current.matchingTotal - removed.length),
    availableTotal: Math.max(
      0,
      current.availableTotal - removed.filter((prospect) => Boolean(prospect.linkedinUrl)).length
    ),
    awaitingReviewTotal: Math.max(0, current.awaitingReviewTotal - removed.length),
  };
}
